import { useCallback, useEffect, useState } from "react";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";
import { X, Upload, Trash2, ChevronLeft, ChevronRight, Video, Images } from "lucide-react";

// Per-kind upload limits — keep in sync with backend (server.py update_catalogue caps)
const KIND_LIMITS = {
  catalogue: { images: 30, videos: 0 },
  combo: { images: 30, videos: 6 },
  trousseau: { images: 30, videos: 6 },
};

// The 3 unified showcase slugs — one tile per category. Admin only manages these.
const UNIFIED_SLUGS = ["previous-works", "combo-showcase", "trousseau"];

const KIND_META = [
  { kind: "catalogue", title: "Previous Works", maxImages: 30, maxVideos: 0 },
  { kind: "combo", title: "Combo Party Orders (Hyderabad)", maxImages: 30, maxVideos: 6 },
  { kind: "trousseau", title: "Trousseau Packing (Hyderabad)", maxImages: 30, maxVideos: 6 },
];

export default function AdminCatalogues() {
  const [catalogues, setCatalogues] = useState([]);
  const [catBusy, setCatBusy] = useState({});
  const [selectedCatImgs, setSelectedCatImgs] = useState({});
  const [dragCatImg, setDragCatImg] = useState(null);

  const reload = useCallback(
    () => adminApi().get("/catalogues").then((r) => setCatalogues(r.data || [])).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const updateLocalCat = (slug, patch) => {
    setCatalogues((prev) => prev.map((c) => (c.slug === slug ? { ...c, ...patch } : c)));
  };

  const uploadCatImage = async (slug, idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setCatBusy((b) => ({ ...b, [`${slug}-${idx}`]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${API}${up.data.url.replace(/^\/api/, "")}`;
      const cat = catalogues.find((c) => c.slug === slug);
      const imgs = [...(cat?.images || [])];
      imgs[idx] = fullUrl;
      const maxImages = KIND_LIMITS[cat?.kind || "catalogue"]?.images || 4;
      const trimmed = imgs.slice(0, maxImages);
      await adminApi().put(`/admin/catalogues/${slug}`, { images: trimmed });
      updateLocalCat(slug, { images: trimmed });
      toast.success("Image saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setCatBusy((b) => ({ ...b, [`${slug}-${idx}`]: false }));
    }
  };

  const removeCatImage = async (slug, idx) => {
    if (!window.confirm("Remove this image?")) return;
    const cat = catalogues.find((c) => c.slug === slug);
    const imgs = (cat?.images || []).filter((_, i) => i !== idx);
    try {
      await adminApi().put(`/admin/catalogues/${slug}`, { images: imgs });
      updateLocalCat(slug, { images: imgs });
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const bulkUploadCatImages = async (slug, files) => {
    if (!files || files.length === 0) return;
    const cat = catalogues.find((c) => c.slug === slug);
    const kind = cat?.kind || "catalogue";
    const max = KIND_LIMITS[kind]?.images || 4;
    const current = (cat?.images || []).filter(Boolean);
    const remaining = max - current.length;
    if (remaining <= 0) { toast.error(`This already has the max ${max} images.`); return; }
    const slice = Array.from(files).slice(0, remaining);
    setCatBusy((b) => ({ ...b, [`${slug}-bulk`]: true }));
    const added = [];
    try {
      for (const f of slice) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`Skipped ${f.name}: over 5MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${API}${up.data.url.replace(/^\/api/, "")}`);
      }
      if (added.length === 0) return;
      const next = [...current, ...added].slice(0, max);
      await adminApi().put(`/admin/catalogues/${slug}`, { images: next });
      updateLocalCat(slug, { images: next });
      toast.success(`${added.length} image${added.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setCatBusy((b) => ({ ...b, [`${slug}-bulk`]: false }));
    }
  };

  const reorderCatImage = async (slug, from, to) => {
    const cat = catalogues.find((c) => c.slug === slug);
    const arr = (cat?.images || []).filter(Boolean);
    if (to < 0 || to >= arr.length || from === to) return;
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    updateLocalCat(slug, { images: next });
    try {
      await adminApi().put(`/admin/catalogues/${slug}`, { images: next });
    } catch {
      toast.error("Could not reorder");
      updateLocalCat(slug, { images: arr });
    }
  };

  const bulkDeleteCatImages = async (slug) => {
    const set = selectedCatImgs[slug] || new Set();
    if (set.size === 0) return;
    if (!window.confirm(`Delete ${set.size} selected image${set.size > 1 ? "s" : ""}?`)) return;
    const cat = catalogues.find((c) => c.slug === slug);
    const next = (cat?.images || []).filter((_, i) => !set.has(i));
    try {
      await adminApi().put(`/admin/catalogues/${slug}`, { images: next });
      updateLocalCat(slug, { images: next });
      setSelectedCatImgs((prev) => ({ ...prev, [slug]: new Set() }));
      toast.success(`Deleted ${set.size}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const toggleCatImgSelected = (slug, idx) => {
    setSelectedCatImgs((prev) => {
      const set = new Set(prev[slug] || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      return { ...prev, [slug]: set };
    });
  };

  const uploadCatVideo = async (slug, idx, file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error("Video must be under 30MB"); return; }
    setCatBusy((b) => ({ ...b, [`${slug}-v${idx}`]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${API}${up.data.url.replace(/^\/api/, "")}`;
      const cat = catalogues.find((c) => c.slug === slug);
      const vids = [...(cat?.videos || [])];
      vids[idx] = fullUrl;
      const maxVideos = KIND_LIMITS[cat?.kind || "catalogue"]?.videos || 0;
      const trimmed = vids.slice(0, maxVideos);
      await adminApi().put(`/admin/catalogues/${slug}`, { videos: trimmed });
      updateLocalCat(slug, { videos: trimmed });
      toast.success("Video saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setCatBusy((b) => ({ ...b, [`${slug}-v${idx}`]: false }));
    }
  };

  const removeCatVideo = async (slug, idx) => {
    if (!window.confirm("Remove this video?")) return;
    const cat = catalogues.find((c) => c.slug === slug);
    const vids = (cat?.videos || []).filter((_, i) => i !== idx);
    try {
      await adminApi().put(`/admin/catalogues/${slug}`, { videos: vids });
      updateLocalCat(slug, { videos: vids });
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const saveCatText = async (slug) => {
    const cat = catalogues.find((c) => c.slug === slug);
    if (!cat) return;
    setCatBusy((b) => ({ ...b, [`${slug}-text`]: true }));
    try {
      await adminApi().put(`/admin/catalogues/${slug}`, {
        description: cat.description || "",
        important_notes: cat.important_notes || "",
        product_info: cat.product_info || "",
      });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setCatBusy((b) => ({ ...b, [`${slug}-text`]: false })); }
  };

  const softDeleteCatalogue = async (c) => {
    if (!window.confirm(`Move "${c.name}" to Recently Deleted?\n\nYou can restore it any time within 30 days from the "Recently Deleted" tab. After that it will be permanently removed.`)) return;
    try {
      await adminApi().delete(`/admin/catalogues/${c.slug}`);
      toast.success(`Moved "${c.name}" to Recently Deleted`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="mt-6" data-testid="admin-catalogues-tab">
      <p className="text-sm text-[#5C5555] mb-4">Upload photos of your previous works to each of the 3 category tiles. Drag any image to reorder · tick the checkbox to select · click <b>Delete (N)</b> to bulk-delete. <b>Previous Works</b> shows up to 30 images on <code className="bg-[#F2E8E4] px-1.5 py-0.5 rounded">/catalogues</code>. <b>Combo Party Orders</b> supports 30 images + 6 videos on <code className="bg-[#F2E8E4] px-1.5 py-0.5 rounded">/combos</code>. <b>Trousseau Packing</b> supports 30 images + 6 videos on <code className="bg-[#F2E8E4] px-1.5 py-0.5 rounded">/trousseau</code>.</p>
      {KIND_META.map(({ kind, title, maxImages, maxVideos }) => {
        const kindCats = catalogues.filter((c) => (c.kind || "catalogue") === kind && UNIFIED_SLUGS.includes(c.slug));
        return (
          <div key={kind} className="mb-8">
            <h3 className="font-serif text-2xl mb-3 text-[#7A1F2B] uppercase tracking-wider">{title} <span className="text-xs text-[#5C5555] normal-case font-sans">({kindCats.length})</span></h3>
            <div className="space-y-4">
              {kindCats.map((c) => {
                const busyText = !!catBusy[`${c.slug}-text`];
                return (
                  <div key={c.slug} className="bg-white border border-[#E5DBD8] p-4 rounded-lg" data-testid={`cat-row-${c.slug}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                      <p className="font-serif text-lg text-[#2D2424]">{c.name}</p>
                      {c.hyderabad_only && (
                        <span className="text-[10px] bg-[#FFD93D] text-[#7A1F2B] px-2 py-0.5 rounded-full border border-[#7A1F2B] font-doodle uppercase tracking-wider">Hyderabad only</span>
                      )}
                    </div>
                    <p className="text-[10px] text-[#5C5555] uppercase tracking-widest">/{c.slug}</p>

                    {/* Bulk upload + bulk delete bar */}
                    {(() => {
                      const filled = (c.images || []).filter(Boolean).length;
                      const remaining = maxImages - filled;
                      const bulkBusy = !!catBusy[`${c.slug}-bulk`];
                      const selSet = selectedCatImgs[c.slug] || new Set();
                      return (
                        <div className="flex items-center justify-between flex-wrap gap-2 mt-3 px-3 py-2 bg-[#FFF6F0] border border-dashed border-[#7A1F2B]/30 rounded">
                          <p className="text-xs text-[#5C5555]"><span className="font-semibold text-[#2D2424]">{filled}/{maxImages}</span> images · <span className="text-[#7A1F2B]">{remaining} remaining</span>{selSet.size > 0 && <span className="ml-2 text-[#7A1F2B]">· {selSet.size} selected</span>}</p>
                          <div className="flex items-center gap-2">
                            {selSet.size > 0 && (
                              <button
                                type="button"
                                onClick={() => bulkDeleteCatImages(c.slug)}
                                data-testid={`cat-bulk-delete-${c.slug}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded hover:bg-[#5C1520]"
                              >
                                <Trash2 className="w-3.5 h-3.5"/> Delete ({selSet.size})
                              </button>
                            )}
                            <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded cursor-pointer hover:bg-[#5C1520] ${remaining <= 0 || bulkBusy ? "opacity-50 cursor-not-allowed" : ""}`}>
                              <Images className="w-3.5 h-3.5"/> {bulkBusy ? "Uploading…" : "Upload multiple"}
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                disabled={remaining <= 0 || bulkBusy}
                                className="hidden"
                                data-testid={`cat-bulk-upload-${c.slug}`}
                                onChange={(e) => { bulkUploadCatImages(c.slug, e.target.files); e.target.value = ""; }}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Image slots */}
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mt-2">
                      {Array.from({ length: maxImages }).map((_, idx) => {
                        const url = (c.images || [])[idx];
                        const busy = !!catBusy[`${c.slug}-${idx}`];
                        const filledCount = (c.images || []).filter(Boolean).length;
                        const canMoveLeft = !!url && idx > 0;
                        const canMoveRight = !!url && idx < filledCount - 1;
                        const isSelected = (selectedCatImgs[c.slug] || new Set()).has(idx);
                        const isDragging = dragCatImg && dragCatImg.slug === c.slug && dragCatImg.from === idx;
                        const isDropTarget = dragCatImg && dragCatImg.slug === c.slug && dragCatImg.over === idx && dragCatImg.from !== idx;
                        return (
                          <div
                            key={idx}
                            className={`relative aspect-square border rounded overflow-hidden group transition-all ${isDropTarget ? "border-[#7A1F2B] border-2 ring-2 ring-[#7A1F2B]/30 scale-[1.03]" : "border-[#E5DBD8]"} ${isDragging ? "opacity-40" : ""} ${isSelected ? "ring-2 ring-[#7A1F2B] border-[#7A1F2B]" : ""} bg-[#FFE7F1]`}
                            draggable={!!url && filledCount > 1}
                            onDragStart={() => url && setDragCatImg({ slug: c.slug, from: idx, over: idx })}
                            onDragEnter={(e) => { if (dragCatImg && dragCatImg.slug === c.slug && url) { e.preventDefault(); setDragCatImg((d) => ({ ...d, over: idx })); } }}
                            onDragOver={(e) => { if (dragCatImg && dragCatImg.slug === c.slug && url) e.preventDefault(); }}
                            onDrop={(e) => {
                              if (dragCatImg && dragCatImg.slug === c.slug && url) {
                                e.preventDefault();
                                const from = dragCatImg.from;
                                setDragCatImg(null);
                                if (from !== idx) reorderCatImage(c.slug, from, idx);
                              }
                            }}
                            onDragEnd={() => setDragCatImg(null)}
                          >
                            {url ? (
                              <>
                                <img src={url} alt="" className="w-full h-full object-cover pointer-events-none"/>
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-white/95 border border-[#2D2424] text-[10px] font-doodle text-[#7A1F2B] leading-none">#{idx + 1}</span>
                                <label className="absolute top-1 left-9 inline-flex items-center justify-center w-5 h-5 rounded bg-white/95 border border-[#2D2424] cursor-pointer hover:bg-[#FFE7F1]" title="Select for bulk delete">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleCatImgSelected(c.slug, idx)}
                                    data-testid={`cat-img-select-${c.slug}-${idx}`}
                                    className="w-3.5 h-3.5 accent-[#7A1F2B] cursor-pointer"
                                  />
                                </label>
                                <button
                                  type="button"
                                  onClick={() => removeCatImage(c.slug, idx)}
                                  data-testid={`cat-img-remove-${c.slug}-${idx}`}
                                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center hover:bg-[#FFE7F1]"
                                  aria-label="remove"
                                ><X className="w-3.5 h-3.5"/></button>
                                {filledCount > 1 && (
                                  <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                    <button
                                      type="button"
                                      disabled={!canMoveLeft}
                                      onClick={() => reorderCatImage(c.slug, idx, idx - 1)}
                                      data-testid={`cat-img-move-left-${c.slug}-${idx}`}
                                      className="w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FFE7F1]"
                                      aria-label="move left"
                                    ><ChevronLeft className="w-3.5 h-3.5"/></button>
                                    <button
                                      type="button"
                                      disabled={!canMoveRight}
                                      onClick={() => reorderCatImage(c.slug, idx, idx + 1)}
                                      data-testid={`cat-img-move-right-${c.slug}-${idx}`}
                                      className="w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FFE7F1]"
                                      aria-label="move right"
                                    ><ChevronRight className="w-3.5 h-3.5"/></button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <label className={`absolute inset-0 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-[#FFD6E7] text-[#7A1F2B] text-xs font-hand ${busy ? "opacity-60" : ""}`}>
                                <Upload className="w-4 h-4"/>
                                <span className="text-[10px]">{busy ? "…" : `Image ${idx + 1}`}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  disabled={busy}
                                  className="hidden"
                                  data-testid={`cat-img-upload-${c.slug}-${idx}`}
                                  onChange={(e) => { uploadCatImage(c.slug, idx, e.target.files?.[0]); e.target.value = ""; }}
                                />
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Video slots — shown when kind supports videos (combo, trousseau) */}
                    {maxVideos > 0 && (
                      <div className="mt-3" data-testid={`cat-videos-${c.slug}`}>
                        <p className="text-[10px] font-doodle uppercase tracking-widest text-[#7A1F2B] flex items-center gap-1.5 mb-1">
                          <Video className="w-3 h-3"/> Videos (max {maxVideos}, ≤ 30MB each)
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {Array.from({ length: maxVideos }).map((_, idx) => {
                            const url = (c.videos || [])[idx];
                            const busy = !!catBusy[`${c.slug}-v${idx}`];
                            return (
                              <div key={idx} className="relative aspect-video border border-[#E5DBD8] bg-[#2D2424]/5 rounded overflow-hidden">
                                {url ? (
                                  <>
                                    <video src={url} className="w-full h-full object-cover" muted preload="metadata"/>
                                    <button
                                      type="button"
                                      onClick={() => removeCatVideo(c.slug, idx)}
                                      data-testid={`cat-vid-remove-${c.slug}-${idx}`}
                                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center hover:bg-[#FFE7F1]"
                                      aria-label="remove"
                                    ><X className="w-3.5 h-3.5"/></button>
                                  </>
                                ) : (
                                  <label className={`absolute inset-0 flex flex-col items-center justify-center gap-0.5 cursor-pointer hover:bg-[#FFD6E7] text-[#7A1F2B] text-xs font-hand ${busy ? "opacity-60" : ""}`}>
                                    <Upload className="w-4 h-4"/>
                                    <span className="text-[10px]">{busy ? "…" : `Video ${idx + 1}`}</span>
                                    <input
                                      type="file"
                                      accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                                      disabled={busy}
                                      className="hidden"
                                      data-testid={`cat-vid-upload-${c.slug}-${idx}`}
                                      onChange={(e) => { uploadCatVideo(c.slug, idx, e.target.files?.[0]); e.target.value = ""; }}
                                    />
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Description + Notes (+ Product Info for combos) */}
                    <div className={`grid ${kind === "combo" ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3 mt-3`}>
                      <label className="text-xs uppercase tracking-wider text-[#5C5555]">Description
                        <textarea
                          value={c.description || ""}
                          onChange={(e) => updateLocalCat(c.slug, { description: e.target.value })}
                          rows={3}
                          placeholder="What this combo / catalogue is about…"
                          data-testid={`cat-desc-${c.slug}`}
                          className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 text-sm bg-white rounded focus:outline-none focus:border-[#7A1F2B] resize-none normal-case tracking-normal text-[#2D2424]"
                        />
                      </label>
                      {kind === "combo" && (
                        <label className="text-xs uppercase tracking-wider text-[#5C5555]">What&apos;s included (Product info)
                          <textarea
                            value={c.product_info || ""}
                            onChange={(e) => updateLocalCat(c.slug, { product_info: e.target.value })}
                            rows={3}
                            placeholder="Cake · table grazing · photographer · decor · return gifts"
                            data-testid={`cat-product-info-${c.slug}`}
                            className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 text-sm bg-white rounded focus:outline-none focus:border-[#7A1F2B] resize-none normal-case tracking-normal text-[#2D2424]"
                          />
                        </label>
                      )}
                      <label className="text-xs uppercase tracking-wider text-[#5C5555]">Important Notes
                        <textarea
                          value={c.important_notes || ""}
                          onChange={(e) => updateLocalCat(c.slug, { important_notes: e.target.value })}
                          rows={3}
                          placeholder="Lead time, themes, customisation rules…"
                          data-testid={`cat-notes-${c.slug}`}
                          className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 text-sm bg-white rounded focus:outline-none focus:border-[#7A1F2B] resize-none normal-case tracking-normal text-[#2D2424]"
                        />
                      </label>
                    </div>

                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={busyText}
                        onClick={() => saveCatText(c.slug)}
                        data-testid={`cat-save-${c.slug}`}
                        className="px-4 py-1.5 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded hover:bg-[#5C1520] disabled:opacity-50"
                      >
                        {busyText ? "Saving…" : "Save text"}
                      </button>
                      <button
                        type="button"
                        onClick={() => softDeleteCatalogue(c)}
                        data-testid={`cat-delete-${c.slug}`}
                        className="px-3 py-1.5 border border-[#7A1F2B] text-[#7A1F2B] text-xs uppercase tracking-wider rounded hover:bg-[#FFE7F1] inline-flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5"/> Move to trash
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
